import api from './api';
import { saveAs } from '../utils/saveFile';

/**
 * Fuel Pump Pricing — Pilot truck-stop prices + driver-region tagging.
 *
 * Sites come from the daily Pilot "Better Of" price sheet (manual upload for
 * now, FTP later). Region tags decide which drivers see a site in gwimove:
 * untagged or inactive sites never reach a driver. Region ids are
 * safetynew.region recnos — the same key on the driver record.
 */

export interface RegionOption {
  id: number;
  name: string;
}

export interface PumpSiteRow {
  recno: number;
  site: string;
  city: string;
  st: string;
  retailPrice: number | null;
  yourPrice: number | null;
  savingsTotal: number | null;
  /** ISO yyyy-mm-dd — the sheet's "Effective Date". */
  priceEffectiveDate: string | null;
  /** ISO datetime — when a file containing this site was last ingested. */
  lastUpdated: string | null;
  /** false = dropped out of the latest file; hidden from drivers. */
  active: boolean;
  regions: RegionOption[];
}

export interface PriceFileRow {
  recno: number;
  filename: string;
  source: string;
  account: string;
  priceEffectiveDate: string | null;
  retailPriceAsOf: string | null;
  rowCount: number;
  insertedCount: number;
  updatedCount: number;
  deactivatedCount: number;
  uploadedBy: string;
  uploadedAt: string | null;
}

export interface UploadResult {
  rows: number;
  inserted: number;
  updated: number;
  deactivated: number;
  account: string;
  priceEffectiveDate: string | null;
  retailPriceAsOf: string | null;
  /** True when this was a forced import of a sheet no newer than what was loaded. */
  stale: boolean;
  warnings: string[];
}

/** 409 body when the sheet is older (or undated) — the caller confirms and retries. */
export interface StaleFileDetail {
  code: 'stale_price_file';
  message: string;
  incomingEffectiveDate: string | null;
  loadedEffectiveDate: string | null;
}

/** Narrows an upload rejection to the staleness gate. */
export const getStaleFileDetail = (err: any): StaleFileDetail | null => {
  const detail = err?.response?.status === 409 ? err?.response?.data?.detail : null;
  return detail?.code === 'stale_price_file' ? (detail as StaleFileDetail) : null;
};

const BASE = '/fuel-pump-prices';

const fuelPumpPricingService = {
  async getSites(signal?: AbortSignal): Promise<PumpSiteRow[]> {
    const res = await api.get(`${BASE}/sites`, { signal });
    return (res.data?.rows ?? []) as PumpSiteRow[];
  },

  async getRegions(signal?: AbortSignal): Promise<RegionOption[]> {
    const res = await api.get(`${BASE}/regions`, { signal });
    return (res.data?.data ?? []) as RegionOption[];
  },

  async setSiteRegions(recno: number, regionIds: number[]): Promise<void> {
    await api.put(`${BASE}/sites/${recno}/regions`, { regionIds });
  },

  /** Upload a sheet. `force` re-posts past the older-than-loaded 409 gate. */
  async uploadFile(file: File, force = false): Promise<UploadResult> {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post<UploadResult>(`${BASE}/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: force ? { force: true } : undefined,
    });
    return data;
  },

  async getFiles(signal?: AbortSignal): Promise<PriceFileRow[]> {
    const res = await api.get(`${BASE}/files`, { signal });
    return (res.data?.rows ?? []) as PriceFileRow[];
  },

  /** Download an archived sheet under its original filename. */
  async downloadFile(row: PriceFileRow): Promise<void> {
    await saveAs(row.filename, async () => {
      const response = await api.get(`${BASE}/files/${row.recno}/download`, {
        responseType: 'blob',
      });
      return new Blob([response.data]);
    });
  },

  /** Delete archived files uploaded before the given ISO date (rows + blobs). */
  async purgeFiles(olderThan: string): Promise<number> {
    const { data } = await api.delete(`${BASE}/files`, {
      params: { older_than: olderThan },
    });
    return data?.deleted ?? 0;
  },
};

export default fuelPumpPricingService;
